import { JSX, ReactNode, createContext, useContext, useMemo } from "react";
import { PaletteMode, ThemeOptions, useMediaQuery } from "@mui/material";
import { useViewer } from "~/providers/viewer";
import {
  darkColorBlindTheme,
  darkTheme,
  lightColorBlindTheme,
  lightTheme,
  foundationLightTheme,
  printTheme,
} from "~/themes";
import { LoadViewerDocument } from "~/operations";
import { SettingsBrightness } from "@mui/icons-material";
import {
  DarkModeOutlined as DarkModeIcon,
  LightModeOutlined as LightModeIcon,
} from "@mui/icons-material";
import { useFeatureFlags } from "./FeatureFlagsProvider";
export type ColorModeSetting = PaletteMode | "system";

export const COLOR_MODE_OPTIONS: Array<{
  value: ColorModeSetting;
  label: string;
  icon: JSX.Element;
}> = [
  { value: "light", label: "Light", icon: <LightModeIcon /> },
  { value: "dark", label: "Dark", icon: <DarkModeIcon /> },
  { value: "system", label: "System", icon: <SettingsBrightness /> },
];

type ColorModeProviderProps = {
  children: ReactNode;
};

type ColorModeContextTypes = {
  mode: PaletteMode;
  colorMode: ColorModeSetting;
  setColorMode: (value: ColorModeSetting) => Promise<void> | void;
  colorBlind: string | undefined; // true / false in key:value pair
  getThemeFromProvider: () => ThemeOptions;
};

const ColorModeContext = createContext<ColorModeContextTypes>({
  mode: "dark",
  colorMode: "dark",
  setColorMode: () => {},
  colorBlind: "false",
  getThemeFromProvider(): ThemeOptions {
    return darkTheme;
  },
});

function ColorModeProvider({ children }: ColorModeProviderProps) {
  const { isFeatureEnabled } = useFeatureFlags();
  const { viewerSettings, updateViewerSettings } = useViewer();
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");

  const colorMode: ColorModeSetting = (viewerSettings?.colormode ||
    "dark") as ColorModeSetting;

  const mode: PaletteMode = useMemo(() => {
    if (colorMode === "system") {
      return prefersDark ? "dark" : "light";
    }
    return colorMode as PaletteMode;
  }, [colorMode, prefersDark]);

  const colorBlind = viewerSettings?.colorblind;

  const getThemeFromProvider = (): ThemeOptions => {
    if (window.location.pathname === "/print") return printTheme;

    if (mode === "light") {
      if (colorBlind === "true") {
        return lightColorBlindTheme;
      }

      if (isFeatureEnabled("Foundation themes")) {
        return foundationLightTheme;
      }
      return lightTheme;
    } else if (mode === "dark") {
      if (colorBlind === "true") {
        return darkColorBlindTheme;
      }
      return darkTheme;
    } else {
      return darkTheme;
    }
  };

  const setColorMode = async (value: ColorModeSetting) => {
    await updateViewerSettings({
      variables: { key: "colormode", value },
      refetchQueries: [LoadViewerDocument],
    });
  };

  return (
    <ColorModeContext.Provider
      value={{
        mode,
        colorMode,
        setColorMode,
        colorBlind,
        getThemeFromProvider,
      }}
    >
      {children}
    </ColorModeContext.Provider>
  );
}

function useColorMode() {
  const context = useContext(ColorModeContext);
  if (context === undefined) {
    throw new Error("useColorMode must be used within a ColorModeProvider");
  }
  return context;
}

export { ColorModeProvider, useColorMode };
